import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import type { SoberStreak } from '@/types'
import Modal from '@/components/layout/Modal'
import { notificationsSupported, notificationsEnabled, getNotificationPermission, requestNotificationPermission } from '@/utils/notifications'
import {
  MILESTONES,
  TIER_COLORS,
  TIER_LABELS,
  computeEarnedMilestones,
  getLiveStreakSeconds,
  getNextMilestone,
  getCurrentMilestone,
  getPersonalBestStatus,
  formatStreakCompact,
  formatGranularDuration,
  liverRecoveryPercent,
  LIVER_RECOVERY_DECIMALS,
  LIVER_RECOVERY_SECONDS,
  type Milestone,
  type EarnedMilestone,
  type BadgeTier,
  type PersonalBestStatus,
} from '@/utils/milestones'
import { toCompactDuration } from '@/utils/time'

interface AwardsProps {
  soberStreaks: SoberStreak[]
  currentBAC: number
  onClose: () => void
}

export default function Awards({ soberStreaks, currentBAC, onClose }: AwardsProps) {
  const earned = useMemo(() => computeEarnedMilestones(soberStreaks), [soberStreaks])
  const earnedIds = useMemo(() => new Set(earned.map(e => e.id)), [earned])
  const liveStreak = getLiveStreakSeconds(soberStreaks, currentBAC)
  const nextMilestone = getNextMilestone(liveStreak)
  const currentMilestone = useMemo(() => getCurrentMilestone(liveStreak), [liveStreak])
  const pbStatus = useMemo(() => getPersonalBestStatus(soberStreaks), [soberStreaks])
  const isSober = currentBAC <= 0
  const [selectedBadge, setSelectedBadge] = useState<{ milestone: Milestone; earned?: EarnedMilestone } | null>(null)
  const [showEarnedModal, setShowEarnedModal] = useState(false)
  const [showLiverModal, setShowLiverModal] = useState(false)
  const [showPbModal, setShowPbModal] = useState(false)
  const [notifState, setNotifState] = useState<'prompt' | 'dismissed' | 'enabled' | 'denied'>(
    () => {
      if (!notificationsSupported()) return 'dismissed'
      if (notificationsEnabled()) return 'enabled'
      if (getNotificationPermission() === 'denied') return 'denied'
      return 'prompt'
    }
  )

  const handleEnableNotifications = useCallback(async () => {
    const granted = await requestNotificationPermission()
    setNotifState(granted ? 'enabled' : 'denied')
  }, [])

  // Group milestones by tier
  const tiers: { tier: BadgeTier; milestones: Milestone[] }[] = useMemo(() => {
    const groups: Record<string, Milestone[]> = {}
    for (const m of MILESTONES) {
      if (!groups[m.tier]) groups[m.tier] = []
      groups[m.tier].push(m)
    }
    return Object.entries(groups).map(([tier, milestones]) => ({
      tier: tier as BadgeTier,
      milestones,
    }))
  }, [])

  const earnedCount = earned.length
  const totalCount = MILESTONES.length

  const getEarnedData = (id: string): EarnedMilestone | undefined =>
    earned.find(e => e.id === id)

  const handleBadgeClick = (m: Milestone) => {
    const earnedData = getEarnedData(m.id)
    setSelectedBadge({ milestone: m, earned: earnedData })
  }

  return (
    <>
      <div className="flex-1 p-4 max-w-4xl lg:max-w-6xl mx-auto w-full">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">Awards and Milestones</h1>
            <button
              onClick={onClose}
              className="text-sm text-[var(--accent-teal)] hover:underline cursor-pointer"
            >
              Back to Dashboard
            </button>
          </div>
          <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
            Every sober day is an achievement. Earn badges as you hit milestones — from your first 24 hours through to years of clarity. Tap any badge to learn more about what it means for your health and recovery.
          </p>
        </div>

        {/* Notification prompt */}
        {notifState === 'prompt' && (
          <div className="card border border-[var(--accent-teal)]/30 flex items-center gap-3">
            <svg className="w-8 h-8 text-[var(--accent-teal)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-[var(--text-primary)] font-medium">Get notified when you hit milestones</p>
              <p className="text-xs text-[var(--text-secondary)]">Even when you're not looking at the app</p>
            </div>
            <button onClick={handleEnableNotifications} className="btn btn-primary text-sm py-1.5 px-3 shrink-0">
              Enable
            </button>
            <button onClick={() => setNotifState('dismissed')} className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] p-1 cursor-pointer">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Top row — 65% (Current Tier + Milestones Earned) / 35% (Personal Best) */}
          <div className="relative overflow-x-clip">
            {isSober && (
              <div
                className="absolute pointer-events-none"
                style={{ inset: '-120px', zIndex: 0 }}
              >
                <ConfettiBurst />
              </div>
            )}
            <div
              className="grid grid-cols-1 md:grid-cols-[65fr_35fr] gap-4 relative"
              style={{ zIndex: 1 }}
            >
              {/* Left 65% — Current Tier + Milestones Earned, 50/50 within */}
              <div className="grid grid-cols-2 gap-4">
                <CurrentTierCard
                  milestone={currentMilestone}
                  onClick={(m) => handleBadgeClick(m)}
                />
                <button
                  type="button"
                  onClick={() => setShowEarnedModal(true)}
                  className="card text-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
                >
                  <p className="text-3xl font-bold text-[var(--text-primary)]">
                    {earnedCount}<span className="text-lg text-[var(--text-secondary)] font-normal"> / {totalCount}</span>
                  </p>
                  <p className="text-sm text-[var(--text-secondary)]">milestones earned</p>
                  {liveStreak > 0 && (
                    <p className="text-sm text-[var(--accent-teal)] mt-1">
                      Current streak: {toCompactDuration(liveStreak)}
                    </p>
                  )}
                  <p className="text-[10px] text-[var(--text-secondary)] mt-2 opacity-70">Tap to view</p>
                </button>
              </div>

              {/* Right 35% — Personal Best */}
              <PersonalBestCard status={pbStatus} onClick={() => setShowPbModal(true)} />
            </div>
          </div>

          {/* Next Milestone + Liver Recovery — 50/50 row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {nextMilestone && liveStreak > 0 ? (
              <div
                className="card border border-[var(--accent-teal)]/30 cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
                onClick={() => handleBadgeClick(nextMilestone)}
              >
                <p className="text-xs text-[var(--accent-teal)] font-semibold uppercase tracking-wide mb-2">Next Milestone</p>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{nextMilestone.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--text-primary)] font-medium truncate">{nextMilestone.name}</p>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {formatStreakCompact(nextMilestone.durationSeconds - liveStreak)} to go
                    </p>
                  </div>
                  <span
                    className="text-xs font-bold uppercase shrink-0"
                    style={{ color: TIER_COLORS[nextMilestone.tier] }}
                  >
                    {TIER_LABELS[nextMilestone.tier]} {nextMilestone.tierLevel}
                  </span>
                </div>
                <div className="mt-3 h-2 bg-[var(--bg-primary)] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{
                      width: `${Math.min(100, (liveStreak / nextMilestone.durationSeconds) * 100)}%`,
                      backgroundColor: TIER_COLORS[nextMilestone.tier],
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="card flex items-center justify-center text-center">
                <p className="text-sm text-[var(--text-secondary)]">
                  {liveStreak > 0 ? 'All milestones earned — keep going!' : 'Start a sober streak to begin earning milestones.'}
                </p>
              </div>
            )}

            {liveStreak > 0 ? (
              <LiverRecoveryBar streakSeconds={liveStreak} onClick={() => setShowLiverModal(true)} />
            ) : (
              <div
                className="card flex items-center justify-center text-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
                onClick={() => setShowLiverModal(true)}
              >
                <p className="text-sm text-[var(--text-secondary)]">
                  Liver recovery starts when your next streak does. Tap to learn how to support it.
                </p>
              </div>
            )}
          </div>

          {/* Badge grid by tier */}
          {tiers.map(({ tier, milestones }) => {
            const tierColor = TIER_COLORS[tier]
            const anyEarned = milestones.some(m => earnedIds.has(m.id))

            return (
              <div key={tier}>
                <h2
                  className="text-sm font-bold uppercase tracking-wide mb-3"
                  style={{ color: anyEarned ? tierColor : 'var(--text-secondary)' }}
                >
                  {TIER_LABELS[tier]}
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {milestones.map(m => {
                    const isEarned = earnedIds.has(m.id)
                    const earnedData = isEarned ? getEarnedData(m.id) : undefined
                    const isActive = isEarned && liveStreak >= m.durationSeconds
                    const isLapsed = isEarned && !isActive
                    const isNext = nextMilestone?.id === m.id

                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handleBadgeClick(m)}
                        className={`rounded-xl p-3 text-center transition-all cursor-pointer relative ${
                          isActive
                            ? 'bg-[var(--bg-secondary)] shadow-lg hover:shadow-xl hover:scale-105'
                            : isLapsed
                              ? 'bg-[var(--bg-secondary)] hover:scale-105'
                              : isNext
                                ? 'bg-[var(--bg-secondary)] border border-dashed border-[var(--border)] hover:bg-[var(--bg-card)]'
                                : 'bg-[var(--bg-secondary)] opacity-40 hover:opacity-60'
                        }`}
                      >
                        {/* Lapsed indicator - sits outside the dimmed content */}
                        {isLapsed && (
                          <span className="absolute top-1.5 right-1.5 z-10 w-5 h-5 rounded-full bg-[var(--accent-orange)] flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6" />
                            </svg>
                          </span>
                        )}
                        {/* Content wrapper - dimmed for lapsed */}
                        <div style={isLapsed ? { opacity: 0.15 } : undefined}>
                        {/* Badge circle */}
                        <div
                          className={`w-14 h-14 rounded-full mx-auto mb-2 flex items-center justify-center text-2xl ${
                            isActive ? 'shadow-md' : ''
                          }`}
                          style={isEarned ? {
                            background: `radial-gradient(circle at 30% 30%, ${tierColor}${isActive ? '40' : '20'}, ${tierColor}${isActive ? '15' : '08'})`,
                            border: `2px solid ${tierColor}${isActive ? '' : '80'}`,
                          } : {
                            background: 'var(--bg-card)',
                            border: '2px solid var(--border)',
                          }}
                        >
                          {isEarned ? m.icon : (
                            <svg className="w-6 h-6 text-[var(--border)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          )}
                        </div>

                        {/* Label */}
                        <p className={`text-xs font-medium leading-tight ${
                          isEarned ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                        }`}>
                          {m.name}
                        </p>

                        {/* Tier level */}
                        <p
                          className="text-[10px] font-bold uppercase mt-0.5"
                          style={{ color: isEarned ? tierColor : 'var(--text-secondary)' }}
                        >
                          {TIER_LABELS[tier]} {m.tierLevel}
                        </p>

                        {/* Earned date or time remaining */}
                        {isEarned && earnedData && (
                          <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                            {new Date(earnedData.earnedAt).toLocaleDateString()}
                          </p>
                        )}
                        {!isEarned && liveStreak > 0 && m.durationSeconds > liveStreak && (
                          <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                            {formatStreakCompact(m.durationSeconds - liveStreak)} to go
                          </p>
                        )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {/* Health recovery timeline */}
          {earned.length > 0 && (
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wide text-[var(--text-secondary)] mb-3">
                Your Recovery Journey
              </h2>
              <div className="space-y-0">
                {earned.map((m, i) => (
                  <div key={m.id} className="flex gap-3 cursor-pointer hover:bg-[var(--bg-secondary)]/50 rounded-lg -mx-2 px-2 py-1 transition-colors" onClick={() => handleBadgeClick(m)}>
                    {/* Timeline line */}
                    <div className="flex flex-col items-center">
                      <div
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: TIER_COLORS[m.tier] }}
                      />
                      {i < earned.length - 1 && (
                        <div className="w-0.5 flex-1 bg-[var(--border)]" />
                      )}
                    </div>
                    {/* Content */}
                    <div className="pb-3">
                      <p className="text-sm text-[var(--text-primary)] font-medium leading-tight">{m.name}</p>
                      <p className="text-xs text-[var(--text-secondary)] leading-relaxed mt-0.5">{m.healthFact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Badge detail modal */}
      {selectedBadge && (
        <BadgeModal
          milestone={selectedBadge.milestone}
          earned={selectedBadge.earned}
          liveStreak={liveStreak}
          onClose={() => setSelectedBadge(null)}
        />
      )}

      {/* Earned milestones list modal */}
      {showEarnedModal && (
        <EarnedMilestonesModal
          earned={earned}
          liveStreak={liveStreak}
          onClose={() => setShowEarnedModal(false)}
          onBadgeClick={(m, e) => {
            setSelectedBadge({ milestone: m, earned: e })
            setShowEarnedModal(false)
          }}
        />
      )}

      {/* Liver recovery details modal */}
      {showLiverModal && (
        <LiverRecoveryModal
          streakSeconds={liveStreak}
          onClose={() => setShowLiverModal(false)}
        />
      )}

      {/* Personal best details modal */}
      {showPbModal && (
        <PersonalBestModal
          status={pbStatus}
          onClose={() => setShowPbModal(false)}
        />
      )}
    </>
  )
}

// ── Badge Detail Modal ──

interface BadgeModalProps {
  milestone: Milestone
  earned?: EarnedMilestone
  liveStreak: number
  onClose: () => void
}

function BadgeModal({ milestone, earned, liveStreak, onClose }: BadgeModalProps) {
  const tierColor = TIER_COLORS[milestone.tier]
  const tierLabel = `${TIER_LABELS[milestone.tier]} ${milestone.tierLevel}`
  const isEarned = !!earned
  const isActive = isEarned && liveStreak >= milestone.durationSeconds
  const isLapsed = isEarned && !isActive
  const remaining = milestone.durationSeconds - liveStreak
  const progress = liveStreak > 0 ? Math.min(100, (liveStreak / milestone.durationSeconds) * 100) : 0

  return (
    <Modal isOpen onClose={onClose} title={milestone.name}>
      <div className="space-y-5">
        {/* Badge */}
        <div className="text-center">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-4xl shadow-lg"
            style={isEarned ? {
              background: `radial-gradient(circle at 30% 30%, ${tierColor}40, ${tierColor}15)`,
              border: `3px solid ${tierColor}`,
            } : {
              background: 'var(--bg-card)',
              border: '3px solid var(--border)',
            }}
          >
            {isEarned ? milestone.icon : (
              <svg className="w-8 h-8 text-[var(--border)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            )}
          </div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: tierColor }}>
            {tierLabel}
          </p>
        </div>

        {/* Status */}
        {isActive ? (
          <div className="p-3 bg-[var(--bac-safe)]/10 border border-[var(--bac-safe)]/30 rounded-lg text-center">
            <p className="text-[var(--bac-safe)] font-medium">Active</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Last earned {new Date(earned!.earnedAt).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        ) : isLapsed ? (
          <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg text-center">
            <p className="text-[var(--accent-orange)] font-medium">Previously Earned</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Last earned {new Date(earned!.earnedAt).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {liveStreak > 0 && remaining > 0 && (
              <p className="text-xs text-[var(--accent-teal)] mt-1">
                {formatStreakCompact(remaining)} to re-activate
              </p>
            )}
          </div>
        ) : liveStreak > 0 && remaining > 0 ? (
          <div>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-[var(--text-secondary)]">Progress</span>
              <span className="text-[var(--text-primary)] font-mono">{formatStreakCompact(remaining)} to go</span>
            </div>
            <div className="h-2.5 bg-[var(--bg-card)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, backgroundColor: tierColor }}
              />
            </div>
          </div>
        ) : (
          <div className="p-3 bg-[var(--bg-card)] rounded-lg text-center">
            <p className="text-sm text-[var(--text-secondary)]">Not yet earned</p>
            <p className="text-xs text-[var(--text-secondary)] mt-0.5">
              Requires {toCompactDuration(milestone.durationSeconds)} of sobriety
            </p>
          </div>
        )}

        {/* What it means */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">What this means for your body</h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {milestone.healthFact}
          </p>
        </div>

        {/* Encouragement */}
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            {isActive
              ? `Reaching ${milestone.name} is a genuine accomplishment. This milestone represents real, measurable improvement in your physical and mental health. Be proud of what you've achieved.`
              : isLapsed
                ? `You've proven you can reach ${milestone.name} — you've done it before. A setback doesn't erase that strength. This badge is waiting for you to light it up again.`
                : `${milestone.name} is within your reach. Every hour of sobriety is your body healing, your mind clearing, and your strength growing. You've got this.`
            }
          </p>
        </div>
      </div>
    </Modal>
  )
}

// ── Liver Recovery Bar ──

interface LiverRecoveryBarProps {
  streakSeconds: number
  onClick?: () => void
}

function LiverRecoveryBar({ streakSeconds, onClick }: LiverRecoveryBarProps) {
  const percent = liverRecoveryPercent(streakSeconds)
  const display = percent.toFixed(LIVER_RECOVERY_DECIMALS)
  const monthsTotal = Math.round(LIVER_RECOVERY_SECONDS / (30 * 86400))

  const Wrapper: 'button' | 'div' = onClick ? 'button' : 'div'

  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`card text-left w-full ${onClick ? 'cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors' : ''}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(239, 68, 68, 0.12)' }}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.8}
            style={{ color: 'var(--bac-danger)' }}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Liver Recovery</p>
            <p
              className="text-sm font-mono tabular-nums shrink-0"
              style={{ color: 'var(--bac-danger)' }}
              aria-live="off"
            >
              {display}%
            </p>
          </div>
          <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-3">
            Liver fat starts dropping within days. Enzymes normalise over weeks. Liver function may fully recover after roughly {monthsTotal} months of sustained sobriety — this bar ticks up every second.
          </p>
          <div
            className="h-2 bg-[var(--bg-primary)] rounded-full overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Liver recovery progress"
          >
            <div
              className="h-full rounded-full"
              style={{
                width: `${percent}%`,
                background: 'linear-gradient(90deg, var(--bac-danger), var(--accent-orange), var(--bac-safe))',
              }}
            />
          </div>
          {onClick && (
            <p className="text-[10px] text-[var(--text-secondary)] mt-2 opacity-70">Tap for recovery details</p>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

// ── Current Tier Card ──

interface CurrentTierCardProps {
  milestone: Milestone | null
  onClick: (m: Milestone) => void
}

function CurrentTierCard({ milestone, onClick }: CurrentTierCardProps) {
  if (!milestone) {
    return (
      <div className="card text-center flex flex-col items-center justify-center">
        <div
          className="w-14 h-14 rounded-full mb-2 flex items-center justify-center"
          style={{ background: 'var(--bg-card)', border: '2px solid var(--border)' }}
        >
          <svg className="w-6 h-6 text-[var(--border)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wide">Current Tier</p>
        <p className="text-sm text-[var(--text-secondary)] mt-1">Not yet earned</p>
      </div>
    )
  }
  const tierColor = TIER_COLORS[milestone.tier]
  return (
    <button
      type="button"
      onClick={() => onClick(milestone)}
      className="card text-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors flex flex-col items-center justify-center"
    >
      <div
        className="w-14 h-14 rounded-full mb-2 flex items-center justify-center text-2xl shadow-md"
        style={{
          background: `radial-gradient(circle at 30% 30%, ${tierColor}40, ${tierColor}15)`,
          border: `2px solid ${tierColor}`,
        }}
      >
        {milestone.icon}
      </div>
      <p className="text-xs uppercase tracking-wide font-bold" style={{ color: tierColor }}>
        {TIER_LABELS[milestone.tier]} {milestone.tierLevel}
      </p>
      <p className="text-sm text-[var(--text-primary)] font-medium mt-0.5 leading-tight">{milestone.name}</p>
      <p className="text-[10px] text-[var(--text-secondary)] mt-1 opacity-70">Current tier</p>
    </button>
  )
}

// ── Personal Best Card ──

interface PersonalBestCardProps {
  status: PersonalBestStatus | null
  onClick: () => void
}

function PersonalBestCard({ status, onClick }: PersonalBestCardProps) {
  const tierColor = TIER_COLORS[PERSONAL_BEST_TIER]

  if (!status) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="card flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
      >
        <span className="text-3xl mb-1">🥇</span>
        <p className="text-xs uppercase tracking-wide font-bold" style={{ color: tierColor }}>
          Personal Best
        </p>
        <p className="text-xs text-[var(--text-secondary)] mt-2 leading-relaxed">
          Complete your first sober streak to set a record to beat.
        </p>
        <p className="text-[10px] text-[var(--text-secondary)] mt-2 opacity-70">Tap for details</p>
      </button>
    )
  }

  if (status.isCurrent) {
    // Active — current live streak holds the record
    return (
      <button
        type="button"
        onClick={onClick}
        className="card text-center flex flex-col items-center justify-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
        style={{ borderColor: tierColor, borderWidth: 1, borderStyle: 'solid' }}
      >
        <div
          className="w-14 h-14 rounded-full mb-2 flex items-center justify-center text-3xl shadow-lg"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${tierColor}55, ${tierColor}20)`,
            border: `2px solid ${tierColor}`,
          }}
        >
          🥇
        </div>
        <p className="text-xs uppercase tracking-wide font-bold" style={{ color: tierColor }}>
          Personal Best
        </p>
        <p
          className="text-sm font-extrabold tracking-widest mt-1"
          style={{ color: tierColor }}
        >
          CURRENT!
        </p>
        <p
          className="text-sm font-mono tabular-nums text-[var(--text-primary)] mt-2 break-words leading-tight"
          aria-live="off"
        >
          {formatGranularDuration(status.liveStreakSeconds)}
        </p>
        <p className="text-[10px] text-[var(--text-secondary)] mt-1 opacity-70">and counting · tap for details</p>
      </button>
    )
  }

  // Inactive — show circular progress toward beating the previous record
  const progress = status.prevDurationSeconds > 0
    ? Math.min(100, (status.liveStreakSeconds / status.prevDurationSeconds) * 100)
    : 0
  const progressLabel = Math.floor(progress)
  const achievedDate = new Date(status.prevAchievedAt).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })

  return (
    <button
      type="button"
      onClick={onClick}
      className="card flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[var(--bg-card)]/50 transition-colors"
    >
      <p className="text-xs uppercase tracking-wide font-bold mb-2" style={{ color: tierColor }}>
        Personal Best
      </p>
      <CircularProgress percent={progress} label={`${progressLabel}%`} color={tierColor} />
      <p className="text-[10px] text-[var(--text-secondary)] mt-1 opacity-80">progress to PB</p>
      <p className="text-sm font-mono tabular-nums text-[var(--text-primary)] mt-2 break-words leading-tight">
        {formatGranularDuration(status.prevDurationSeconds)}
      </p>
      <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">last achieved on {achievedDate}</p>
      <p className="text-[10px] text-[var(--text-secondary)] mt-2 opacity-70">Tap for details</p>
    </button>
  )
}

const PERSONAL_BEST_TIER: BadgeTier = 'gold'

// ── Circular Progress (SVG ring with centered label) ──

interface CircularProgressProps {
  percent: number  // 0-100
  label: string
  color: string
  size?: number
}

function CircularProgress({ percent, label, color, size = 84 }: CircularProgressProps) {
  const r = 38
  const circumference = 2 * Math.PI * r
  const clamped = Math.max(0, Math.min(100, percent))
  const offset = circumference * (1 - clamped / 100)
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" width={size} height={size} className="-rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--bg-card)" strokeWidth="8" />
        <circle
          cx="50" cy="50" r={r} fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-base font-bold tabular-nums text-[var(--text-primary)]">{label}</span>
      </div>
    </div>
  )
}

// ── Personal Best Modal ──

interface PersonalBestModalProps {
  status: PersonalBestStatus | null
  onClose: () => void
}

function PersonalBestModal({ status, onClose }: PersonalBestModalProps) {
  const tierColor = TIER_COLORS[PERSONAL_BEST_TIER]

  const dateLong = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })

  return (
    <Modal isOpen onClose={onClose} title="Personal Best">
      <div className="space-y-5">
        {/* Medal header */}
        <div className="text-center">
          <div
            className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center text-4xl shadow-lg"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${tierColor}55, ${tierColor}20)`,
              border: `3px solid ${tierColor}`,
            }}
          >
            🥇
          </div>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: tierColor }}>
            Longest Sober Streak
          </p>
        </div>

        {!status ? (
          /* No prior record yet */
          <div className="p-3 bg-[var(--bg-card)] rounded-lg text-center">
            <p className="text-sm text-[var(--text-primary)] font-medium">No record set yet</p>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-1">
              Complete your first sober streak and it becomes the record to beat. From then on, every new streak is measured against your personal best.
            </p>
          </div>
        ) : status.isCurrent ? (
          /* Live streak holds the record */
          <PbCurrentBody status={status} tierColor={tierColor} dateLong={dateLong} />
        ) : (
          /* Chasing the previous record */
          <PbChasingBody status={status} tierColor={tierColor} dateLong={dateLong} />
        )}

        {/* Encouragement */}
        {status && (
          <div className="p-3 bg-[var(--bg-card)] rounded-lg">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {status.isCurrent
                ? "You're in uncharted territory — every second from here is a brand-new personal best. Keep going."
                : status.liveStartedAt
                  ? "You've done it before, which means you can do it again. Stay the course and the record is yours."
                  : "Your record is proof of what you're capable of. When you're ready, start a new streak and chase it down."}
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}

interface PbBodyProps {
  status: PersonalBestStatus
  tierColor: string
  dateLong: (iso: string) => string
}

function PbCurrentBody({ status, tierColor, dateLong }: PbBodyProps) {
  const aheadBy = Math.max(0, status.liveStreakSeconds - status.prevDurationSeconds)
  return (
    <>
      <div
        className="p-4 rounded-lg text-center"
        style={{ background: `${tierColor}15`, border: `1px solid ${tierColor}55` }}
      >
        <p className="text-xs font-extrabold uppercase tracking-widest" style={{ color: tierColor }}>
          New record — and counting
        </p>
        <p className="text-lg font-mono tabular-nums text-[var(--text-primary)] mt-1 break-words leading-tight">
          {formatGranularDuration(status.liveStreakSeconds)}
        </p>
        {status.liveStartedAt && (
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Streak started {dateLong(status.liveStartedAt)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">Old record</p>
          <p className="text-sm font-mono tabular-nums text-[var(--text-primary)] mt-1 break-words leading-tight">
            {formatGranularDuration(status.prevDurationSeconds)}
          </p>
        </div>
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">Ahead by</p>
          <p className="text-sm font-mono tabular-nums mt-1 break-words leading-tight" style={{ color: tierColor }}>
            {formatGranularDuration(aheadBy)}
          </p>
        </div>
      </div>
      <p className="text-xs text-[var(--text-secondary)] text-center -mt-2">
        Beaten on {dateLong(new Date(new Date(status.liveStartedAt!).getTime() + status.prevDurationSeconds * 1000).toISOString())}
      </p>
    </>
  )
}

function PbChasingBody({ status, tierColor, dateLong }: PbBodyProps) {
  const progress = status.prevDurationSeconds > 0
    ? Math.min(100, (status.liveStreakSeconds / status.prevDurationSeconds) * 100)
    : 0
  const remaining = Math.max(0, status.prevDurationSeconds - status.liveStreakSeconds)
  // Calendar moment the record would be beaten, if a streak is currently live
  const projectedBeatIso = status.liveStartedAt
    ? new Date(new Date(status.liveStartedAt).getTime() + status.prevDurationSeconds * 1000).toISOString()
    : null

  return (
    <>
      {/* The headline: time left to beat the record */}
      {status.liveStartedAt ? (
        <div
          className="p-4 rounded-lg text-center"
          style={{ background: `${tierColor}12`, border: `1px solid ${tierColor}40` }}
        >
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">Until you beat your record</p>
          <p className="text-lg font-mono tabular-nums font-bold mt-1 break-words leading-tight" style={{ color: tierColor }}>
            {formatGranularDuration(remaining)}
          </p>
          {projectedBeatIso && (
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              On track to beat it {dateLong(projectedBeatIso)}
            </p>
          )}
        </div>
      ) : (
        <div className="p-4 rounded-lg text-center bg-[var(--bg-card)]">
          <p className="text-sm text-[var(--text-primary)] font-medium">No active streak</p>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed mt-1">
            Start a new streak and stay sober for{' '}
            <span className="font-mono tabular-nums text-[var(--text-primary)]">{formatGranularDuration(status.prevDurationSeconds)}</span>{' '}
            to set a new personal best.
          </p>
        </div>
      )}

      {/* Progress ring */}
      <div className="flex items-center justify-center gap-4">
        <CircularProgress percent={progress} label={`${Math.floor(progress)}%`} color={tierColor} />
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">Current streak</p>
          <p className="text-sm font-mono tabular-nums text-[var(--text-primary)] break-words leading-tight">
            {status.liveStreakSeconds > 0 ? formatGranularDuration(status.liveStreakSeconds) : '—'}
          </p>
        </div>
      </div>

      {/* Record details */}
      <div className="p-3 bg-[var(--bg-card)] rounded-lg">
        <p className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)] mb-1">Your record</p>
        <p className="text-sm font-mono tabular-nums text-[var(--text-primary)] break-words leading-tight">
          {formatGranularDuration(status.prevDurationSeconds)}
        </p>
        <p className="text-xs text-[var(--text-secondary)] mt-1">
          {dateLong(status.prevStartedAt)} → {dateLong(status.prevAchievedAt)}
        </p>
      </div>
    </>
  )
}

// ── Earned Milestones Modal ──

interface EarnedMilestonesModalProps {
  earned: EarnedMilestone[]
  liveStreak: number
  onClose: () => void
  onBadgeClick: (m: Milestone, earned: EarnedMilestone) => void
}

function EarnedMilestonesModal({ earned, liveStreak, onClose, onBadgeClick }: EarnedMilestonesModalProps) {
  const sorted = [...earned].sort((a, b) => b.durationSeconds - a.durationSeconds)
  return (
    <Modal isOpen onClose={onClose} title={`Earned Milestones (${earned.length})`}>
      {earned.length === 0 ? (
        <p className="text-sm text-[var(--text-secondary)] text-center py-4">
          No milestones earned yet. Your first 24 hours of sobriety unlocks the first badge.
        </p>
      ) : (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto -mx-2 px-2">
          {sorted.map(m => {
            const tierColor = TIER_COLORS[m.tier]
            const isActive = liveStreak >= m.durationSeconds
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onBadgeClick(m, m)}
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--bg-card)] transition-colors text-left"
              >
                <div
                  className="w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-xl"
                  style={{
                    background: `radial-gradient(circle at 30% 30%, ${tierColor}${isActive ? '40' : '20'}, ${tierColor}${isActive ? '15' : '08'})`,
                    border: `2px solid ${tierColor}${isActive ? '' : '80'}`,
                    opacity: isActive ? 1 : 0.7,
                  }}
                >
                  {m.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--text-primary)] font-medium leading-tight truncate">{m.name}</p>
                  <p className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                    <span style={{ color: tierColor }} className="font-bold">
                      {TIER_LABELS[m.tier]} {m.tierLevel}
                    </span>
                    {' · Earned '}
                    {new Date(m.earnedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    {!isActive && ' · Lapsed'}
                  </p>
                </div>
                <svg className="w-4 h-4 text-[var(--text-secondary)] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )
          })}
        </div>
      )}
    </Modal>
  )
}

// ── Liver Recovery Modal ──

interface LiverRecoveryModalProps {
  streakSeconds: number
  onClose: () => void
}

function LiverRecoveryModal({ streakSeconds, onClose }: LiverRecoveryModalProps) {
  const percent = liverRecoveryPercent(streakSeconds)
  const display = percent.toFixed(LIVER_RECOVERY_DECIMALS)
  const monthsTotal = Math.round(LIVER_RECOVERY_SECONDS / (30 * 86400))

  return (
    <Modal isOpen onClose={onClose} title="Liver Recovery">
      <div className="space-y-5">
        {/* Live percentage */}
        <div className="text-center">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center"
            style={{ background: 'rgba(239, 68, 68, 0.12)' }}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8} style={{ color: 'var(--bac-danger)' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>
          <p className="text-2xl font-mono tabular-nums font-bold" style={{ color: 'var(--bac-danger)' }}>
            {display}%
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            of the path to fully normalised liver function
          </p>
        </div>

        {/* Recovery timeline */}
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Recovery timeline</h3>
          <ul className="text-sm text-[var(--text-secondary)] leading-relaxed space-y-1.5 list-disc pl-5">
            <li><span className="text-[var(--text-primary)] font-medium">Day 1–3:</span> blood alcohol clears, hydration improves, and the liver stops processing toxins.</li>
            <li><span className="text-[var(--text-primary)] font-medium">Week 1–2:</span> liver enzymes (GGT, ALT, AST) start trending down. Inflammation drops.</li>
            <li><span className="text-[var(--text-primary)] font-medium">Month 1:</span> liver fat can drop by up to 15% with sustained sobriety.</li>
            <li><span className="text-[var(--text-primary)] font-medium">Months 2–5:</span> enzyme levels continue normalising. Fatty deposits keep shrinking.</li>
            <li><span className="text-[var(--text-primary)] font-medium">~{monthsTotal} months:</span> for many people, liver function returns to a fully normal baseline.</li>
          </ul>
        </div>

        {/* Eat well */}
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">🥗 Eat well to support recovery</h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Sobriety is the biggest lever, but the liver heals fastest when fed properly. Lean protein supplies the amino acids needed to rebuild liver tissue. Leafy greens, cruciferous vegetables, berries, nuts, and oily fish supply antioxidants that protect against further damage. Limit added sugar, refined carbs, and ultra-processed foods — they drive the same fat accumulation that alcohol did.
          </p>
        </div>

        {/* Move your body */}
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">🏃 Move your body</h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Regular exercise independently reduces liver fat — even without weight loss. Aim for 150 minutes of moderate aerobic activity per week (walking, cycling, swimming) plus a couple of strength sessions. Consistency matters far more than intensity.
          </p>
        </div>

        {/* Other levers */}
        <div className="p-3 bg-[var(--bg-card)] rounded-lg">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">💧 Other levers</h3>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Stay hydrated, prioritise 7–9 hours of sleep, manage weight if it's elevated, and check with your doctor before starting new medications or supplements — many are processed by the liver and some (like high-dose paracetamol) are dose-sensitive.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="p-3 bg-[var(--accent-orange)]/10 border border-[var(--accent-orange)]/30 rounded-lg">
          <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
            <span className="text-[var(--accent-orange)] font-semibold">Note:</span> the percentage above is an educational estimate based on a typical {monthsTotal}-month recovery curve. Individual outcomes vary widely depending on prior drinking history, age, genetics, and existing liver damage. If you have symptoms or a diagnosed liver condition, consult your doctor — they can run blood tests to track real recovery.
          </p>
        </div>
      </div>
    </Modal>
  )
}

// ── Confetti Burst (page-mounted celebration) ──

interface ConfettiParticle {
  x: number
  y: number
  vx: number
  vy: number
  color: string
  size: number
  rotation: number
  rotationSpeed: number
  opacity: number
}

function ConfettiBurst() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    if (!parent) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = parent.clientWidth
    canvas.height = parent.clientHeight

    const colors = ['#2dd4bf', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#22c55e', '#FFD700', '#f8fafc']
    const particles: ConfettiParticle[] = []
    const cx = canvas.width / 2
    const cy = canvas.height / 2

    // One big radial burst
    for (let i = 0; i < 180; i++) {
      const angle = (Math.PI * 2 * i) / 180 + (Math.random() - 0.5) * 0.4
      const speed = 4 + Math.random() * 9
      particles.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.25,
        opacity: 1,
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      let active = false
      for (const p of particles) {
        if (p.opacity <= 0) continue
        active = true
        p.x += p.vx
        p.vy += 0.18 // gravity
        p.y += p.vy
        p.rotation += p.rotationSpeed
        p.opacity -= 0.0035
        p.vx *= 0.99

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.globalAlpha = Math.max(0, p.opacity)
        ctx.fillStyle = p.color
        if (p.size > 6) {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        } else {
          ctx.beginPath()
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
          ctx.fill()
        }
        ctx.restore()
      }
      if (active) {
        animRef.current = requestAnimationFrame(animate)
      }
    }

    animRef.current = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animRef.current)
  }, [])

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
}
